/** History: every event with its date, source and corrections. Shared by animals, places, groups, jobs and the ranch log. */
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import type { EventDTO, Page as PageOf } from "../../contract";
import { api, qs } from "../lib/api";
import { formatDate, timeAgo, useFormat } from "../lib/format";
import { t } from "../lib/i18n";
import { registry } from "../registry";
import { Button, Chip, Empty, ErrorNote, Icon, Loading, cx } from "../ui/kit";
import { eventSummary, subjectLine } from "./describe";

export function EventRow({ event, showSubjects = true }: { event: EventDTO; showSubjects?: boolean }) {
  const fmt = useFormat(event.labels);
  const def = registry.event(event.type);
  return (
    <li className={cx("timeline-row", event.status !== "active" && "is-struck")}>
      <span className={cx("timeline-icon", def && `tone-${def.tone}`)}><Icon name={def?.icon ?? "note"} size={16} /></span>
      <Link to={`/history/${event.id}`} className="timeline-main">
        <span className="timeline-title">
          <strong>{def ? t(def.label) : event.type}</strong>
          {event.revision > 1 && <Chip tone="info" icon="edit">{t("Corrected")}</Chip>}
          {event.status === "voided" && <Chip tone="neutral">{t("Removed")}</Chip>}
          {event.source_kind === "import" && <Chip tone="neutral" icon="upload">{t("Imported")}</Chip>}
        </span>
        <span className="timeline-summary">{eventSummary(event, fmt)}</span>
        <span className="timeline-meta">
          {showSubjects && event.subjects.length > 0 && <span>{subjectLine(event, fmt)}</span>}
          {event.recorded_by && <span>{event.recorded_by.label}</span>}
        </span>
      </Link>
      <time className="timeline-date" dateTime={event.occurred_at ?? undefined} title={t("Recorded {{when}}", { when: timeAgo(event.recorded_at) })}>{formatDate(event.occurred_at, event.occurred_precision)}</time>
    </li>
  );
}

/** Paged history. `subject` like "animal:an_123"; `module` filters to one domain. */
export function Timeline({ subject, module, job, filters = true, limit = 40, showSubjects, empty }: { subject?: string; module?: string; job?: string; filters?: boolean; limit?: number; showSubjects?: boolean; empty?: string }) {
  const [only, setOnly] = useState<string | null>(module ?? null);
  const query = useInfiniteQuery({
    queryKey: ["/events", subject, only, job, limit],
    queryFn: ({ pageParam }) => api.get<PageOf<EventDTO>>(`/events${qs({ subject, module: only, job, cursor: pageParam as string | null, limit })}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
  });
  const events = query.data?.pages.flatMap((p) => p.items) ?? [];
  const modules = registry.modules.filter((m) => m.events?.length && m.id !== "core");
  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorNote error={query.error} retry={() => query.refetch()} />;
  return (
    <div className="timeline">
      {filters && !module && (
        <div className="filter-chips" role="group" aria-label={t("Show")}>
          <button type="button" className={cx("choice", !only && "choice-on")} onClick={() => setOnly(null)}>{t("Everything")}</button>
          {modules.map((m) => <button key={m.id} type="button" className={cx("choice", only === m.id && "choice-on")} onClick={() => setOnly(only === m.id ? null : m.id)}>{t(m.label)}</button>)}
        </div>
      )}
      {events.length === 0 ? <Empty icon="history" title={empty ?? t("Nothing recorded yet")} body={t("Tell Hondo what happened and it will show up here.")} /> : (
        <ol className="timeline-list">{events.map((e) => <EventRow key={e.id} event={e} showSubjects={showSubjects ?? !subject} />)}</ol>
      )}
      {query.hasNextPage && <Button variant="ghost" block busy={query.isFetchingNextPage} onClick={() => query.fetchNextPage()}>{t("Show older")}</Button>}
    </div>
  );
}
